import { z } from 'zod';
import type { HistoryEventRecord } from '../../domain/types.js';
import { chatComplete, deepSeekReady } from '../../llm/client.js';
import { parseJsonLoose } from '../../llm/prompts.js';
import {
  AWAY_SPAN_CAP_MS,
  missedConversationCount,
  uncappedMissedConversationCount,
} from '../../sim/conversationPace.js';
import { formatWorldClockPrompt } from './clock.js';
import { uid } from '../spawn.js';
import type { OperatingSchedule } from '../../domain/types.js';

const catchUpResponseSchema = z.object({
  events: z.array(z.object({
    occurredAt: z.string().min(1).optional(),
    scope: z.enum(['location', 'character']),
    characterId: z.string().min(1).optional(),
    summary: z.string().min(1).max(140),
  })),
  resume: z.object({
    facingAway: z.tuple([z.string(), z.string()]).optional(),
  }).optional(),
});

export interface CatchUpArgs {
  worldId: string;
  elapsedRealMs: number;
  lastEvents: HistoryEventRecord[];
  locationDescription: string;
  roster: Array<{ id: string; firstName: string }>;
  relationships: Array<{ familiarity: number; affinity: number }>;
  existing: HistoryEventRecord[];
  /** World clock at the moment the player returns. New notes are placed on this clock. */
  endIso?: string;
  schedule?: OperatingSchedule;
  locationRegion?: string;
}

export interface CatchUpResult {
  events: HistoryEventRecord[];
  resume: { facingAway?: [string, string] } | null;
  eventStory: string | null;
}

/** Split prose on blank lines, drop empties, keep at most four paragraphs. */
export function clampEventStory(text: string): string | null {
  const paragraphs = text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  if (paragraphs.length === 0) return null;
  return paragraphs.slice(0, 4).join('\n\n');
}

/** Hosted chat keeps prose only when the model is asked for JSON. */
export function storyFromModelText(text: string): string | null {
  try {
    const parsed = parseJsonLoose(text) as { paragraphs?: unknown; story?: unknown };
    if (Array.isArray(parsed.paragraphs)) {
      const parts = parsed.paragraphs
        .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
        .map((part) => part.trim());
      if (parts.length > 0) return clampEventStory(parts.join('\n\n'));
    }
    if (typeof parsed.story === 'string' && parsed.story.trim()) return clampEventStory(parsed.story);
  } catch {
    // Plain prose still counts when a test or fallback returns it.
  }
  return clampEventStory(text);
}

async function generateEventStory(
  newEvents: HistoryEventRecord[],
  args: CatchUpArgs,
): Promise<string | null> {
  if (!deepSeekReady()) return null;
  try {
    const idToName = new Map(args.roster.map((npc) => [npc.id, npc.firstName]));
    const notes = newEvents.map((event) => ({
      occurredAt: event.occurredAt,
      summary: event.summary,
      actors: event.actorIds.flatMap((id) => {
        const name = idToName.get(id);
        return name ? [name] : [];
      }),
    }));
    const response = await chatComplete([
      {
        role: 'system',
        content: [
          'Write a short narrative of the missed events listed below.',
          'Use roster first names, not raw ids. Be compelling and accurate.',
          'Do not invent people, places, or events that are not in the notes.',
          'Do not mention line counts, character counts, "compiled from", or "ready to observe".',
          'Do not dump a timestamp log. Prefer one or two paragraphs. Never more than four.',
          'Return JSON only: {"paragraphs":["paragraph one","paragraph two"]}.',
        ].join(' '),
      },
      {
        role: 'user',
        content: JSON.stringify({
          ...(args.endIso && args.schedule
            ? {
                worldClock: formatWorldClockPrompt(
                  args.endIso,
                  args.schedule,
                  args.locationRegion,
                ),
                worldClockNote: 'This clock is the world\'s time.',
              }
            : {}),
          roster: args.roster.map((npc) => npc.firstName),
          notes,
        }),
      },
    ], { json: true, maxTokens: 800, temperature: 0.7 });
    return storyFromModelText(response);
  } catch {
    return null;
  }
}

export async function catchUp(args: CatchUpArgs): Promise<CatchUpResult> {
  const notesToWrite = missedConversationCount(args.elapsedRealMs);
  if (args.elapsedRealMs <= 0 || notesToWrite === 0 || !deepSeekReady()) {
    return { events: args.existing, resume: null, eventStory: null };
  }
  const missedCount = uncappedMissedConversationCount(args.elapsedRealMs);
  let events: HistoryEventRecord[];
  let resume: { facingAway?: [string, string] } | null;
  try {
    const response = await chatComplete([
      {
        role: 'system',
        content: [
          'Return JSON only: {"events":[{"scope":"location"|"character","characterId":"id","summary":"one conversation"}],"resume":{"facingAway":["id","id"]}}.',
          `The player missed ${missedCount} conversations. Write ${notesToWrite} notes. These notes are the ones to write.`,
          'Each note is a different conversation. Each summary is at most 140 characters.',
          'Do not repeat a moment already in history. Do not invent a calendar date. Do not include a line count.',
          'Place missed conversations on the world clock given below; do not invent a different date or timezone.',
        ].join(' '),
      },
      {
        role: 'user',
        content: JSON.stringify({
          missedConversations: missedCount,
          notesToWrite,
          elapsedRealMs: args.elapsedRealMs,
          ...(args.endIso && args.schedule
            ? {
                worldClock: formatWorldClockPrompt(
                  args.endIso,
                  args.schedule,
                  args.locationRegion,
                ),
              }
            : {}),
          history: args.lastEvents.slice(-20).map(({ eventId, occurredAt, summary, actorIds }) => ({
            id: eventId, occurredAt, summary, actorIds,
          })),
          locationDescription: args.locationDescription,
          roster: args.roster,
          relationships: args.relationships,
        }),
      },
    ], { json: true, maxTokens: Math.min(4096, 400 + notesToWrite * 120), temperature: 0.6 });
    const parsed = catchUpResponseSchema.parse(JSON.parse(response));
    const seen = new Set(args.existing.map((event) => event.summary.trim().replace(/\s+/g, ' ').toLowerCase()));
    const fresh = parsed.events.filter((event) => {
      const key = event.summary.trim().replace(/\s+/g, ' ').toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, notesToWrite);
    const end = new Date(args.endIso ?? new Date().toISOString()).getTime();
    const span = Math.min(Math.max(args.elapsedRealMs, 60_000), AWAY_SPAN_CAP_MS);
    const start = end - span;
    const newEvents = fresh.map((event, index) => ({
      eventId: uid('evt'),
      worldId: args.worldId,
      occurredAt: new Date(start + ((index + 1) / fresh.length) * (end - start)).toISOString(),
      summary: event.summary.trim(),
      actorIds: event.scope === 'character' && event.characterId ? [event.characterId] : [],
      deviceIds: [],
      observed: false,
    }));
    events = [...args.existing, ...newEvents];
    resume = parsed.resume ?? null;
  } catch {
    return { events: args.existing, resume: null, eventStory: null };
  }
  const added = events.slice(args.existing.length);
  const eventStory = added.length > 0 ? await generateEventStory(added, args) : null;
  return { events, resume, eventStory };
}
